<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Hoa hồng của tôi'">
          <template v-slot:preview>
            <!-- Bộ lọc kỳ -->
            <b-form class="mb-5">
              <b-row>
                <b-col md="2">
                  <label class="form-label">Từ ngày</label>
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <label class="form-label">Đến ngày</label>
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="3" class="d-flex align-items-end">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-2"
                    :disabled="onLoading"
                    @click="fetchData"
                  >
                    <i class="fas fa-search mr-1"></i>Xem
                  </b-button>
                </b-col>
              </b-row>
            </b-form>

            <vcl-table v-if="onLoading" :columns="5"></vcl-table>

            <div v-else-if="report">
              <!-- Thẻ tổng quan -->
              <b-row class="mb-5">
                <b-col md="3">
                  <div class="stat-card">
                    <div class="stat-card__label">Số lượng sản phẩm</div>
                    <div class="stat-card__value">
                      {{ displayQuantity(report.totalCount) }}
                    </div>
                  </div>
                </b-col>
                <b-col md="3">
                  <div class="stat-card">
                    <div class="stat-card__label">Hoa hồng bán hàng</div>
                    <div class="stat-card__value">
                      {{ displayAmount(report.totalPoint) }}
                    </div>
                  </div>
                </b-col>
                <b-col md="3">
                  <div class="stat-card">
                    <div class="stat-card__label">Cộng / Trừ khác</div>
                    <div class="stat-card__value">
                      <span class="text-success">
                        +{{ displayAmount(report.bonusPoint) }}
                      </span>
                      <span class="text-danger ml-2">
                        −{{ displayAmount(report.minusPoint) }}
                      </span>
                    </div>
                  </div>
                </b-col>
                <b-col md="3">
                  <div class="stat-card stat-card--primary">
                    <div class="stat-card__label">Tổng thực nhận</div>
                    <div
                      class="stat-card__value"
                      :class="amountClass(report.finalPoint)"
                    >
                      {{ displayAmount(report.finalPoint) }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <div class="text-muted mb-4" v-if="report.storeName">
                Cửa hàng: <strong>{{ report.storeName }}</strong>
                &nbsp;·&nbsp; Kỳ:
                <strong>{{ report.fromDate }} — {{ report.toDate }}</strong>
              </div>

              <!-- Chi tiết theo vai trò -->
              <p class="font-weight-bold mb-2">
                Chi tiết theo vai trò ({{ report.listDetail.length }}):
              </p>
              <b-alert
                v-if="!report.listDetail.length"
                show
                variant="secondary"
                class="mb-4"
              >
                Kỳ này chưa phát sinh hoa hồng.
              </b-alert>

              <div
                v-for="role in report.listDetail"
                :key="'role-' + role.jobTitleId"
                class="role-block mb-4"
              >
                <div class="role-block__head">
                  <div>
                    <span class="role-block__name">{{ role.jobTitleName }}</span>
                    <span class="text-muted ml-2">
                      ({{ displayQuantity(role.count) }} sản phẩm)
                    </span>
                  </div>
                  <div class="role-block__total" :class="amountClass(role.rewardPoint)">
                    {{ displayAmount(role.rewardPoint) }}
                  </div>
                </div>

                <b-table
                  class="table-bordered table-hover mb-0"
                  :fields="groupFields"
                  :items="role.listDetail"
                  small
                  show-empty
                  empty-text="Không có nhóm hoa hồng nào"
                >
                  <template v-slot:cell(name)="row">
                    <strong>{{ row.item.name }}</strong>
                  </template>
                  <template v-slot:cell(conditions)="row">
                    <div
                      v-for="(cond, index) in row.item.conditions"
                      :key="'c-' + index"
                      class="cond-row"
                    >
                      <span class="cond-row__name">{{ cond.name }}</span>
                      <span class="cond-row__count">
                        {{ displayQuantity(cond.count) }} SP
                      </span>
                      <span class="cond-row__point" :class="amountClass(cond.point)">
                        {{ displayAmount(cond.point) }}
                      </span>
                      <b-button
                        variant="link"
                        size="sm"
                        class="p-0 ml-2"
                        @click="openDetail(role, row.item, cond)"
                      >
                        Xem hoá đơn
                      </b-button>
                    </div>
                  </template>
                </b-table>
              </div>

              <!-- Cộng / trừ khác -->
              <b-row v-if="report.listBonus.length || report.listMinus.length">
                <b-col md="6" v-if="report.listBonus.length">
                  <p class="font-weight-bold mb-2">
                    Cộng khác ({{ report.listBonus.length }}):
                  </p>
                  <b-table
                    class="table-bordered mb-4"
                    :fields="adjustFields"
                    :items="report.listBonus"
                    small
                  >
                    <template v-slot:cell(amount)="row">
                      <span class="text-success">
                        {{ displayAmount(row.item.amount) }}
                      </span>
                    </template>
                  </b-table>
                </b-col>
                <b-col md="6" v-if="report.listMinus.length">
                  <p class="font-weight-bold mb-2">
                    Trừ khác ({{ report.listMinus.length }}):
                  </p>
                  <b-table
                    class="table-bordered mb-4"
                    :fields="adjustFields"
                    :items="report.listMinus"
                    small
                  >
                    <template v-slot:cell(amount)="row">
                      <span class="text-danger">
                        {{ displayAmount(row.item.amount) }}
                      </span>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </div>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Danh sách hoá đơn của một bậc điều kiện -->
    <b-modal
      v-model="detailModalVisible"
      :title="detailTitle"
      size="xl"
      ok-only
      ok-title="Đóng"
    >
      <vcl-table v-if="detailLoading" :columns="5"></vcl-table>
      <div v-else>
        <div class="mb-3 text-muted">
          {{ detailRows.length }} dòng · Tổng:
          <strong :class="amountClass(detailTotal)">
            {{ displayAmount(detailTotal) }}
          </strong>
        </div>
        <b-table
          class="table-bordered table-hover"
          :fields="billFields"
          :items="detailRows"
          small
          show-empty
          empty-text="Không có hoá đơn nào"
        >
          <template v-slot:cell(billDate)="row">
            {{ displayDate(row.item.billDate) }}
          </template>
          <template v-slot:cell(quantity)="row">
            <span :class="amountClass(row.item.quantity)">
              {{ row.item.quantity }}
            </span>
          </template>
          <template v-slot:cell(point)="row">
            <span :class="amountClass(row.item.point)">
              {{ displayAmount(row.item.point) }}
            </span>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * HOA HỒNG CỦA TÔI — màn web xem hoa hồng của chính người đăng nhập.
 *
 * NT-3 / KT-14: KHÔNG viết API mới. Trang này gọi ĐÚNG hai endpoint mà app mobile
 * đang dùng:
 *   GET /rewardReport/mobile          — tổng hợp theo vai trò → nhóm → bậc điều kiện
 *   GET /rewardReport/mobile/details  — danh sách hoá đơn của một bậc
 *
 * `employeeId` KHÔNG truyền: controller tự lấy từ token, nên trang luôn hiện
 * hoa hồng của chính người đang đăng nhập.
 *
 * Hai API này chạy engine nào là do feature flag `commission-report-mobile-v2`
 * quyết định — web và app vì thế luôn ra cùng một con số.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile } from '@/utils/common';
// Dùng lại helper định dạng của màn báo cáo, không viết lại
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

export default {
  components: { KTCodePreview, VclTable, datePicker },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      report: null,
      filter: {
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
      },
      detailModalVisible: false,
      detailLoading: false,
      detailTitle: '',
      detailRows: [],

      groupFields: [
        {
          key: 'name',
          label: 'Nhóm hoa hồng',
          thStyle: thStyle('28%'),
        },
        {
          key: 'conditions',
          label: 'Bậc điều kiện',
          thStyle: thStyle('72%'),
        },
      ],
      adjustFields: [
        { key: 'date', label: 'Ngày', thStyle: thStyle('22%') },
        { key: 'description', label: 'Nội dung', thStyle: thStyle('56%') },
        {
          key: 'amount',
          label: 'Số tiền',
          thStyle: thStyle('22%'),
          tdClass: 'text-right',
        },
      ],
      billFields: [
        { key: 'billNumber', label: 'Số hoá đơn', thStyle: thStyle('15%') },
        { key: 'billDate', label: 'Ngày', thStyle: thStyle('13%') },
        { key: 'productName', label: 'Sản phẩm', thStyle: thStyle('44%') },
        {
          key: 'quantity',
          label: 'SL',
          thStyle: thStyle('8%'),
          tdClass: 'text-center',
        },
        {
          key: 'point',
          label: 'Hoa hồng',
          thStyle: thStyle('20%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    detailTotal() {
      return this.detailRows.reduce((sum, row) => sum + Number(row.point || 0), 0);
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Hoa hồng của tôi' }]);
  },
  methods: {
    /** §7.5 #1 — Tổng hợp. Không truyền employeeId: BE lấy từ token */
    fetchData() {
      const params = {
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
      if (!params.fromDate || !params.toDate) {
        return makeToastFaile('Vui lòng chọn đầy đủ Từ ngày và Đến ngày!');
      }

      this.onLoading = true;
      ApiService.setHeader();
      ApiService.query('rewardReport/mobile', { params })
        .then(({ data }) => {
          this.report = data.data || null;
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được hoa hồng'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },

    /**
     * §7.5 #2 — Hoá đơn của một bậc điều kiện.
     * `ids` và `roleId` lấy thẳng từ dữ liệu tổng hợp, đúng cách app mobile làm.
     */
    openDetail(role, group, condition) {
      this.detailTitle = `${role.jobTitleName} · ${group.name} · ${condition.name}`;
      this.detailRows = [];
      this.detailModalVisible = true;
      this.detailLoading = true;

      ApiService.setHeader();
      ApiService.query('rewardReport/mobile/details', {
        params: {
          fromDate: this.toApiDate(this.filter.fromDate),
          toDate: this.toApiDate(this.filter.toDate),
          roleId: role.jobTitleId,
          ids: (condition.ids || []).join(','),
        },
      })
        .then(({ data }) => {
          this.detailRows = data.data || [];
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được danh sách hoá đơn'),
          );
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.stat-card {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 16px;
  height: 100%;
}
.stat-card--primary {
  background: #f1faff;
  border-color: #cfe7fb;
}
.stat-card__label {
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 6px;
}
.stat-card__value {
  font-size: 18px;
  font-weight: 700;
  color: #181c32;
}
.role-block {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}
.role-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f3f6f9;
  padding: 10px 16px;
}
.role-block__name {
  font-weight: 600;
  color: #181c32;
}
.role-block__total {
  font-weight: 700;
  font-size: 15px;
}
.cond-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 2px 0;
  font-size: 13px;
}
.cond-row__name {
  flex: 1 1 auto;
  color: #3f4254;
}
.cond-row__count {
  flex: 0 0 80px;
  text-align: right;
  color: #7e8299;
}
.cond-row__point {
  flex: 0 0 110px;
  text-align: right;
  font-weight: 600;
}
</style>
